import React from "react";

import { Modal } from "./Chrome";
import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorPortfolioAccount } from "../../../Types/XFactorViewsRendering";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";
import { BAND_FILL, buildRoPoints } from "../lib/derive";

const W = 900;
const H = 460;
const PAD = { top: 24, right: 40, bottom: 48, left: 64 };

const QUADRANTS = [
  { label: "Stabilise then Grow", x: "left", y: "top", fill: "#fef2f2" },
  { label: "Invest & Grow", x: "right", y: "top", fill: "#f0fdf4" },
  { label: "Defend & Remediate", x: "left", y: "bottom", fill: "#fffbeb" },
  { label: "Develop & Nurture", x: "right", y: "bottom", fill: "#eff6ff" },
] as const;

/**
 * Risk-Opportunity quadrant scatter.
 *
 * The x-axis is inverted — risk runs 100 → 0 left to right — so that "further
 * right" always means "safer", and the desirable quadrant sits top-right. Plot
 * it the conventional way and every quadrant label inverts with it.
 */
const RoChartModal: React.FC<{
  open: boolean;
  onClose: () => void;
  accounts: XFactorPortfolioAccount[];
  onSelect: (digestId: string) => void;
}> = ({ open, onClose, accounts, onSelect }) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const points = React.useMemo(
    () => buildRoPoints(accounts, bhThresholds),
    [accounts, bhThresholds]
  );

  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  // risk 100 → left edge, risk 0 → right edge.
  const xOf = (risk: number) => PAD.left + ((100 - risk) / 100) * plotW;
  const yOf = (opp: number) => PAD.top + ((100 - opp) / 100) * plotH;
  const midX = xOf(50);
  const midY = yOf(50);

  const dropped = accounts.length - points.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="max-w-5xl"
      title="Business Health · R-O Chart"
      subtitle={`Risk-Opportunity quadrant · ${points.length} account${
        points.length === 1 ? "" : "s"
      }`}
    >
      {!points.length ? (
        <p className="py-10 text-center text-sm text-gray-400">
          No account has both a risk and an opportunity score in this window.
        </p>
      ) : (
        <>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full"
            role="img"
            aria-label="Risk-opportunity quadrant scatter"
          >
            {QUADRANTS.map(q => (
              <rect
                key={q.label}
                x={q.x === "left" ? PAD.left : midX}
                y={q.y === "top" ? PAD.top : midY}
                width={
                  q.x === "left" ? midX - PAD.left : PAD.left + plotW - midX
                }
                height={q.y === "top" ? midY - PAD.top : PAD.top + plotH - midY}
                fill={q.fill}
              />
            ))}

            {QUADRANTS.map(q => (
              <text
                key={`${q.label}-label`}
                x={q.x === "left" ? PAD.left + 10 : PAD.left + plotW - 10}
                y={q.y === "top" ? PAD.top + 18 : PAD.top + plotH - 8}
                textAnchor={q.x === "left" ? "start" : "end"}
                className="fill-gray-400"
                style={{ fontSize: 11, fontWeight: 600 }}
              >
                {q.label}
              </text>
            ))}

            <line
              x1={midX}
              y1={PAD.top}
              x2={midX}
              y2={PAD.top + plotH}
              stroke="#cbd5e1"
              strokeDasharray="4 4"
            />
            <line
              x1={PAD.left}
              y1={midY}
              x2={PAD.left + plotW}
              y2={midY}
              stroke="#cbd5e1"
              strokeDasharray="4 4"
            />

            <line
              x1={PAD.left}
              y1={PAD.top + plotH}
              x2={PAD.left + plotW}
              y2={PAD.top + plotH}
              stroke="#94a3b8"
            />
            <line
              x1={PAD.left}
              y1={PAD.top}
              x2={PAD.left}
              y2={PAD.top + plotH}
              stroke="#94a3b8"
            />

            {[0, 25, 50, 75, 100].map(t => (
              <text
                key={`x-${t}`}
                x={xOf(t)}
                y={PAD.top + plotH + 16}
                textAnchor="middle"
                className="fill-gray-400"
                style={{ fontSize: 10 }}
              >
                {t}
              </text>
            ))}
            {[0, 25, 50, 75, 100].map(t => (
              <text
                key={`y-${t}`}
                x={PAD.left - 8}
                y={yOf(t) + 3}
                textAnchor="end"
                className="fill-gray-400"
                style={{ fontSize: 10 }}
              >
                {t}
              </text>
            ))}

            <text
              x={PAD.left + plotW / 2}
              y={H - 10}
              textAnchor="middle"
              className="fill-gray-500"
              style={{ fontSize: 11 }}
            >
              Risk Composite Score (RCS) — lower = lower risk
            </text>
            <text
              x={16}
              y={PAD.top + plotH / 2}
              textAnchor="middle"
              transform={`rotate(-90 16 ${PAD.top + plotH / 2})`}
              className="fill-gray-500"
              style={{ fontSize: 11 }}
            >
              Opportunity Composite Score (OCS)
            </text>

            {points.map(p => (
              <g
                key={p.digestId}
                className="cursor-pointer"
                onClick={() => onSelect(p.digestId)}
              >
                <title>{`${p.accountName} — risk ${Math.round(
                  p.risk
                )}, opportunity ${Math.round(p.opportunity)}`}</title>
                <circle
                  cx={xOf(p.risk)}
                  cy={yOf(p.opportunity)}
                  r={7}
                  fill={BAND_FILL[p.band]}
                  stroke="#fff"
                  strokeWidth={1.5}
                />
                <text
                  x={xOf(p.risk) - 11}
                  y={yOf(p.opportunity) + 3}
                  textAnchor="end"
                  className="fill-gray-700"
                  style={{ fontSize: 11 }}
                >
                  {p.accountName}
                </text>
              </g>
            ))}
          </svg>

          {dropped > 0 ? (
            <p className="mt-3 text-xs italic text-gray-400">
              {dropped} account{dropped === 1 ? "" : "s"} not plotted — missing
              a risk or opportunity score.
            </p>
          ) : null}
        </>
      )}
    </Modal>
  );
};

export default RoChartModal;
